import React, { useState, useCallback, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useDropzone } from 'react-dropzone';
import { FaFile, FaFilePdf, FaFileImage, FaFileWord, FaFileExcel, FaTrash, FaFileAltSlash } from 'react-icons/fa';
import { MdCloudUpload } from 'react-icons/md';
import { toast } from 'react-toastify';
import axios from 'axios';
import { BACKEND } from '../../config/configEnvs';

const DocumentosCotizacion = forwardRef(({ isAdmin, cotizacionId, docuemntoFirmadoInicial, documentos: documentosIniciales, documentoContrato: documentoContratoInicial, pdfContrato: pdfContratoInicial, updateCotizacionLocally }, ref) => {
  
  const [documentos, setDocumentos] = useState(documentosIniciales || []);
  const [loading, setLoading] = useState(false);
  const [documentosParaSubir, setDocumentosParaSubir] = useState([]);
  const [docAsignado, setDocAsignado] = useState(documentoContratoInicial || null);
  const [pdfContrato, setPdfContrato] = useState(pdfContratoInicial || null);
  const [uploadingAsignado, setUploadingAsignado] = useState(false);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [docuemntoFirmado, setDocuemntoFirmado] = useState(docuemntoFirmadoInicial || null);
  const signedUrl = typeof docuemntoFirmado === 'string' ? docuemntoFirmado : docuemntoFirmado?.url;
  const signedMimeType = typeof docuemntoFirmado === 'object' ? (docuemntoFirmado?.mimeType || 'application/pdf') : 'application/pdf';
  const signedFilename = typeof docuemntoFirmado === 'object' && docuemntoFirmado?.filename ? docuemntoFirmado.filename : (signedUrl ? signedUrl.split('/').pop() : "");

  useEffect(() => {
    setDocumentos(documentosIniciales || []);
  }, [documentosIniciales]);

  useEffect(() => {
    setDocAsignado(documentoContratoInicial || null);
  }, [documentoContratoInicial]);

  useEffect(() => {
    setPdfContrato(pdfContratoInicial || null);
  }, [pdfContratoInicial]);

  useEffect(() => {
    return () => {
      documentosParaSubir.forEach(doc => {
        if (doc.preview) {
          URL.revokeObjectURL(doc.preview);
        }
      });
    };
  }, [documentosParaSubir]);

  const onDrop = useCallback((acceptedFiles) => {
    const newDocs = acceptedFiles.map(file => ({
      file,
      id: Date.now() + Math.random(),
      nombre: file.name,
      tipo: file.type,
      tamano: file.size,
      preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null
    }));
    setDocumentosParaSubir(prev => [...prev, ...newDocs]);
  }, []);

  // upload helpers are above drop handlers to satisfy linter ordering

  // No pending cancel states needed; uploads are automatic

  const subirDocumentos = async () => {
    setLoading(true);
    try {
      const documentosSubidos = [];
      // Subir cada documento
      for (const doc of documentosParaSubir) {
        const formData = new FormData();
        formData.append('file', doc.file);
        formData.append('cotizacion', cotizacionId);
        formData.append('nombre', doc.nombre);
        formData.append('tipo', doc.tipo);

        const response = await axios.post(`${BACKEND}/api/documentosCotizaciones`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        if (response.data?.doc) {
          documentosSubidos.push(response.data.doc);
        }
      }

      // Actualizar el estado local con los nuevos documentos
      setDocumentos(prev => [...prev, ...documentosSubidos]);
      
      // Actualizar la cotización con todos los documentos (existentes + nuevos)
      const todosLosDocumentos = [...documentos, ...documentosSubidos].map(doc => doc.id);
      
      await axios.put(`${BACKEND}/api/cotizaciones/${cotizacionId}`, {
        documentos: todosLosDocumentos
      });

      setDocumentosParaSubir([]);
      toast.success('Documentos guardados correctamente');
    } catch (error) {
      console.error('Error al subir documentos:', error);
      toast.error('Error al subir los documentos');
    } finally {
      setLoading(false);
    }
  };

  const subirDocumentoAsignado = useCallback(async (file) => {
    if (!file) return;
    setUploadingAsignado(true);
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('cotizacion', cotizacionId);
      formData.append('nombre', file.name);
      formData.append('tipo', file.type);

      const response = await axios.post(`${BACKEND}/api/documentosCotizaciones`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const uploaded = response.data?.doc || response.data;
      if (uploaded?.id) {
        await axios.put(`${BACKEND}/api/cotizaciones/${cotizacionId}`, {
          documentoContrato: uploaded.id
        });
        setDocAsignado(uploaded);
        if (typeof updateCotizacionLocally === 'function') {
          updateCotizacionLocally({ id: cotizacionId, documentoContrato: uploaded });
        }
        toast.success('Contrato subido correctamente');
      } else {
        throw new Error('Respuesta inesperada al subir contrato');
      }
    } catch (error) {
      console.error('Error al subir contrato:', error);
      toast.error('Error al subir el contrato');
    } finally {
      setUploadingAsignado(false);
      setLoading(false);
    }
  }, [cotizacionId, updateCotizacionLocally]);

  const subirPdfAsignado = useCallback(async (file) => {
    if (!file) return;
    setUploadingPdf(true);
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('cotizacion', cotizacionId);
      formData.append('nombre', file.name);
      formData.append('tipo', file.type);

      const response = await axios.post(`${BACKEND}/api/documentosCotizaciones`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });


      const uploaded = response.data?.doc || response.data;
      if (uploaded?.id) {
        await axios.put(`${BACKEND}/api/cotizaciones/${cotizacionId}`, {
          pdfContrato: uploaded.id
        });
        setPdfContrato(uploaded);
        if (typeof updateCotizacionLocally === 'function') {
          updateCotizacionLocally({ id: cotizacionId, pdfContrato: uploaded });
        }
        toast.success('PDF subido correctamente');
      } else {
        throw new Error('Respuesta inesperada al subir PDF');
      }
    } catch (error) {
      console.error('Error al subir PDF:', error);
      toast.error('Error al subir el PDF');
    } finally {
      setUploadingPdf(false);
      setLoading(false);
    }
  }, [cotizacionId, updateCotizacionLocally]);

  const onDropAsignado = useCallback((acceptedFiles) => {
    if (docAsignado || uploadingAsignado) {
      toast.info('Ya existe un contrato. Elimina el actual para subir otro.');
      return;
    }
    if (!acceptedFiles || acceptedFiles.length === 0) return;
    const file = acceptedFiles[0];
    subirDocumentoAsignado(file);
  }, [docAsignado, uploadingAsignado, subirDocumentoAsignado]);

  const onDropPdf = useCallback((acceptedFiles) => {
    if (pdfContrato || uploadingPdf) {
      toast.info('Ya existe un PDF. Elimina el actual para subir otro.');
      return;
    }
    if (!acceptedFiles || acceptedFiles.length === 0) return;
    const file = acceptedFiles[0];
    subirPdfAsignado(file);
  }, [pdfContrato, uploadingPdf, subirPdfAsignado]);

  const eliminarDocumento = async (docId, esPendiente = false) => {
    if (esPendiente) {
      const doc = documentosParaSubir.find(d => d.id === docId);
      if (doc?.preview) {
        URL.revokeObjectURL(doc.preview);
      }
      setDocumentosParaSubir(prev => prev.filter(d => d.id !== docId));
      return;
    }

    try {
      const response = await axios.delete(`${BACKEND}/api/documentosCotizaciones/${docId}`)

      if (!response.status === 200) {
        throw new Error('Error al eliminar el documento');
      } else {
        // Extraer solo los IDs de los documentos restantes
        const documentosIds = documentos.filter(d => d.id !== docId).map(doc => doc.id);
        
        await axios.put(`${BACKEND}/api/cotizaciones/${cotizacionId}`, {
          documentos: documentosIds
        })

        setDocumentos(prev => prev.filter(d => d.id !== docId));
        toast.success('Documento eliminado correctamente');
      }
    } catch (error) {
      console.error('Error al eliminar documento:', error);
      toast.error('Error al eliminar el documento');
    }
  };

  const eliminarDocumentoAsignado = async () => {
    try {
      const confirm = window.confirm('¿Estás seguro de querer eliminar el contrato de la cotización?');
      if (!confirm) {
        return;
      }
      if (docAsignado?.id) {
        const response = await axios.delete(`${BACKEND}/api/documentosCotizaciones/${docAsignado.id}`);
        if (!response.status === 200) {
          throw new Error('Error al eliminar el documento asignado');
        }
      }
      await axios.put(`${BACKEND}/api/cotizaciones/${cotizacionId}`, { documentoContrato: null });
      setDocAsignado(null);
      if (typeof updateCotizacionLocally === 'function') {
        updateCotizacionLocally({ id: cotizacionId, documentoContrato: null });
      }
      toast.success('Contrato eliminado');
    } catch (error) {
      console.error('Error al eliminar documento asignado:', error);
      toast.error('Error al eliminar el documento asignado');
    }
  };

  const eliminarPdfAsignado = async () => {
    try {
      const confirm = window.confirm('¿Estás seguro de querer eliminar el PDF de la cotización?');
      if (!confirm) {
        return;
      }
      if (pdfContrato?.id) {
        const response = await axios.delete(`${BACKEND}/api/documentosCotizaciones/${pdfContrato.id}`);
        if (!response.status === 200) {
          throw new Error('Error al eliminar el PDF asignado');
        }
      }
      await axios.put(`${BACKEND}/api/cotizaciones/${cotizacionId}`, { pdfContrato: null });
      setPdfContrato(null);
      if (typeof updateCotizacionLocally === 'function') {
        updateCotizacionLocally({ id: cotizacionId, pdfContrato: null });
      }
      toast.success('PDF de cotización eliminado');
    } catch (error) {
      console.error('Error al eliminar PDF asignado:', error);
      toast.error('Error al eliminar el PDF asignado');
    }
  };

  const eliminarDocumentoFirmado = async () => {
    try {
      const confirm = window.confirm('¿Estás seguro de querer eliminar el documento firmado?');
      if (!confirm) {
        return;
      }
      // Intentar borrar el archivo del bucket solo si tenemos ID
      if (docuemntoFirmado && typeof docuemntoFirmado === 'object' && docuemntoFirmado.id) {
        const response = await axios.delete(`${BACKEND}/api/documentosCotizaciones/${docuemntoFirmado.id}`);
        if (!response.status === 200) {
          throw new Error('Error al eliminar el documento firmado');
        }
      }
      // Limpiar referencia en la cotización (normalizando el nombre del campo)
      await axios.put(`${BACKEND}/api/cotizaciones/${cotizacionId}`, { documentoFirmado: null });
      setDocuemntoFirmado(null);
      if (typeof updateCotizacionLocally === 'function') {
        updateCotizacionLocally({ id: cotizacionId, documentoFirmado: null });
      }
      toast.success('Documento firmado de cotización eliminado');
    } catch (error) {
      console.log(error);
      toast.error('Error al eliminar el documento firmado');
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/*': ['.png', '.jpg', '.jpeg', '.gif'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx']
    }
  });

  const { getRootProps: getRootPropsAsignado, getInputProps: getInputPropsAsignado, isDragActive: isDragActiveAsignado } = useDropzone({
    onDrop: onDropAsignado,
    accept: { 'application/pdf': ['.pdf'] },
    multiple: false,
    disabled: Boolean(docAsignado || uploadingAsignado)
  });

  const { getRootProps: getRootPropsPdf, getInputProps: getInputPropsPdf, isDragActive: isDragActivePdf } = useDropzone({
    onDrop: onDropPdf,
    accept: { 'application/pdf': ['.pdf'] },
    multiple: false,
    disabled: Boolean(pdfContrato || uploadingPdf)
  });

  const getIconoDocumento = (tipo) => {
    if (!tipo) return <FaFile className="h-10 w-10 text-gray-400 mx-auto" />;
    
    if (tipo.includes('pdf')) {
      return <FaFilePdf className="h-10 w-10 text-red-500 mx-auto" />;
    }
    if (tipo.includes('image')) {
      return <FaFileImage className="h-10 w-10 text-blue-500 mx-auto" />;
    }
    if (tipo.includes('word') || tipo.includes('document')) {
      return <FaFileWord className="h-10 w-10 text-blue-700 mx-auto" />;
    }
    if (tipo.includes('sheet') || tipo.includes('excel')) {
      return <FaFileExcel className="h-10 w-10 text-green-600 mx-auto" />;
    }
    return <FaFile className="h-10 w-10 text-gray-400 mx-auto" />;
  };

  const formatearTamaño = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  useImperativeHandle(ref, () => ({
    guardarDocumentos: async () => {
      if (documentosParaSubir.length > 0) {
        await subirDocumentos();
      }
    }
  }));

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

        {/* Contrato dropzone with inline states */}
        <div>
          <div 
            {...(!docAsignado && !uploadingAsignado ? getRootPropsAsignado() : {})}
            className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
              uploadingAsignado ? 'border-blue-500 bg-blue-50' : docAsignado ? 'border-gray-300 hover:border-gray-400' : isDragActiveAsignado ? 'border-blue-500 bg-blue-50 cursor-pointer' : 'border-gray-300 hover:border-gray-400 cursor-pointer'
            }`}
          >
            {!docAsignado && !uploadingAsignado && <input {...getInputPropsAsignado()} />}
            {uploadingAsignado ? (
              <div className="space-y-4">
                <div className="mx-auto h-12 w-12 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                <p className="text-sm text-gray-400">Subiendo contrato...</p>
              </div>
            ) : docAsignado ? (
              <div className="flex items-center justify-between space-y-2">
                <div className="space-y-2 w-full flex flex-col items-center justify-between">
                  {getIconoDocumento(docAsignado.mimeType)}
                  <a href={docAsignado.url} target="_blank" rel="noreferrer" className="hover:underline text-sm text-gray-600 truncate">
                    {docAsignado.filename}
                  </a>
                  <p className="text-xs text-gray-500">{formatearTamaño(docAsignado.filesize)}</p>
                </div>
                {isAdmin && (
                  <button onClick={eliminarDocumentoAsignado} className="text-gray-400 hover:text-red-500">
                    <FaTrash className="h-4 w-4" />
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                <MdCloudUpload className="mx-auto h-10 w-10 text-gray-400" />
                <p className="text-sm text-gray-600">
                  Arrastra y suelta el <b className="text-blue-500">contrato de la cotización</b>, o haz clic para seleccionar
                </p>
                <p className="text-xs text-gray-500">Solo PDF (máx. 10MB)</p>
              </div>
            )}
          </div>
        </div>

        {/* PDF dropzone with inline states */}
        {/* <div>
          <div 
            {...(!pdfContrato && !uploadingPdf ? getRootPropsPdf() : {})}
            className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
              uploadingPdf ? 'border-blue-500 bg-blue-50' : pdfContrato ? 'border-gray-300 hover:border-gray-400' : isDragActivePdf ? 'border-blue-500 bg-blue-50 cursor-pointer' : 'border-gray-300 hover:border-gray-400 cursor-pointer'
            }`}
          >
            {!pdfContrato && !uploadingPdf && <input {...getInputPropsPdf()} />}
            {uploadingPdf ? (
              <div className="space-y-4">
                <div className="mx-auto h-12 w-12 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                <p className="text-sm text-gray-600">Subiendo PDF...</p>
              </div>
            ) : pdfContrato ? (
              <div className="flex items-center justify-between space-y-2">
                <div className="space-y-2 w-full flex flex-col items-center justify-between">
                {getIconoDocumento(pdfContrato.mimeType)}
                  <a href={pdfContrato.url} target="_blank" rel="noreferrer" className="hover:underline text-sm text-gray-600 truncate">
                    {pdfContrato.filename}
                  </a>
                  <p className="text-xs text-gray-500">{formatearTamaño(pdfContrato.filesize)}</p>
                </div>
                {isAdmin && (
                  <button onClick={eliminarPdfAsignado} className="text-gray-400 hover:text-red-500">
                    <FaTrash className="h-4 w-4" />
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                <MdCloudUpload className="mx-auto h-10 w-10 text-gray-400" />
                <p className="text-sm text-gray-600">
                  Arrastra y suelta el <b className="text-orange-500">PDF de la cotización</b>, o haz clic para seleccionar
                </p>
                <p className="text-xs text-gray-500">Solo PDF (máx. 10MB)</p>
              </div>
            )}
          </div>
        </div> */}

        {/* Contract uploaded by client (read-only: download if exists, no upload here) */}
        <div>
          <div 
            className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
              docuemntoFirmado ? 'border-gray-300 hover:border-gray-400' : 'border-gray-300'
            }`}
          >
            {docuemntoFirmado ? (
              <div className="flex items-center justify-between space-y-2">
                <div className="space-y-2 w-full flex flex-col items-center justify-between">
                  {getIconoDocumento(signedMimeType)}
                  <a href={signedUrl} target="_blank" rel="noreferrer" className="hover:underline text-sm text-gray-600 truncate">
                    {signedFilename || 'Ver documento firmado'}
                  </a>
                  <p className="text-xs text-gray-500">Contrato firmado por el cliente.</p>
                </div>
                {isAdmin && (
                  <button onClick={eliminarDocumentoFirmado} className="text-gray-400 hover:text-red-500">
                    <FaTrash className="h-4 w-4" />
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                <FaFile className="mx-auto h-10 w-10 text-gray-400" />
                <p className="text-sm text-gray-600">
                  Cuando el cliente suba el contrato firmado, aparecerá aquí.
                </p>
                <p className="text-xs text-gray-500">Solo lectura en este módulo.</p>
              </div>
            )}
          </div>
        </div>

      </div>

      {/* Dropzone general para documentos varios (se mantiene igual) */}
      <div 
        {...getRootProps()} 
        className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors
          ${isDragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'}`}
      >
        <input {...getInputProps()} />
        <div className="space-y-2">
          <FaFileImage className="mx-auto h-12 w-12 text-gray-400" />
          <p className="text-sm text-gray-600">
            Arrastra y suelta archivos aquí, o haz clic para seleccionar
          </p>
          <p className="text-xs text-gray-500">
            PDF, Word, Excel e imágenes (máx. 10MB)
          </p>
        </div>
      </div>

      {loading && (
        <div className="flex justify-center items-center py-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </div>
      )}

      {/* Documentos existentes */}
      {documentos.length > 0 && (
        <div className="space-y-2">
          <h1 className="font-semibold leading-none text-sm text-green-500">
            Documentos guardados
          </h1>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {documentos.map((doc) => (
              <div key={doc.id} className="relative bg-white p-4 rounded-lg border hover">
      
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        {getIconoDocumento(doc.mimeType)}
                        <a 
                          href={doc.url} 
                          className="hover:underline cursor-pointer"
                          target="_blank"
                          rel="noreferrer"
                        >
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900">
                              {doc.filename}
                            </p>
                            <p className="text-xs text-gray-500">{formatearTamaño(doc.filesize)}</p>
                          </div>
                        </a>
                      </div>
                      <div className="mt-2 text-xs text-gray-500">
                        {new Date(doc.createdAt).toLocaleDateString()}
                      </div>
                    </div>
                    {isAdmin && (
                      <button
                        onClick={() => eliminarDocumento(doc.id)}
                        className="ml-2 text-gray-400 hover:text-red-500"
                      >
                        <FaTrash className="h-4 w-4" />
                      </button>
                    )}
                  </div>
             
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Documentos pendientes de subir */}
      {documentosParaSubir.length > 0 && (
        <div className="space-y-2">
          <h1 className="font-semibold leading-none text-sm text-orange-300">
            Documentos pendientes
          </h1>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {documentosParaSubir.map((doc) => (
              <div key={doc.id} className="relative bg-white p-4 rounded-lg border border-orange-200">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      {getIconoDocumento(doc.tipo)}
                      <a 
                        href={doc.preview || URL.createObjectURL(doc.file)} 
                        className="hover:underline cursor-pointer"
                        target="_blank"
                        rel="noreferrer"
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900">
                            {doc.nombre} <span className="text-orange-500">(Pendiente)</span>
                          </p>
                          <p className="text-xs text-gray-500">{formatearTamaño(doc.tamano)}</p>
                        </div>
                      </a>
                    </div>
                    {doc.preview && (
                      <div className="mt-2 h-24 overflow-hidden rounded border">
                        <img 
                          src={doc.preview} 
                          alt={doc.nombre} 
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                  </div>
                  {isAdmin && (
                    <button
                      onClick={() => eliminarDocumento(doc.id, true)}
                      className="ml-2 text-gray-400 hover:text-red-500"
                    >
                      <FaTrash className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
});

export default DocumentosCotizacion;
